import { ApolloClient, gql } from '@apollo/client';

import { CRM_LIST_DETAIL, CRM_SAVE_FILTER } from '../lists/lists.queries';

/**
 * Emailing everyone with a tag. A campaign goes to a list or saved filter, so a
 * tag's email goes to the saved filter "Tagged <name>": the one already saved
 * for exactly that tag, or one saved now. Reusing it keeps the lists page from
 * filling with copies each time someone emails the same tag.
 */

export const CRM_TAG_FILTER_CHOICES = gql`
  query CrmTagFilterChoices($search: String) {
    crmSegments(search: $search, type: DYNAMIC, take: 50) {
      items {
        id
        name
        isActive
      }
    }
  }
`;

export const tagFilterName = (tagName: string) => `Tagged ${tagName}`.slice(0, 200);

/** The saved filter for exactly this tag: everyone carrying it, nothing else. */
export const tagFilter = (tagId: number) => ({ tags: { anyOf: [tagId] } });

/** Whether a stored filter selects exactly the people with this one tag. */
export const isTagFilter = (filter: Record<string, unknown> | null | undefined, tagId: number) => {
  if (!filter) return false;
  const keys = Object.keys(filter).filter((k) => filter[k] != null);
  const tags = filter.tags as { anyOf?: Array<number>; allOf?: unknown; noneOf?: unknown } | undefined;
  return keys.length === 1 && keys[0] === 'tags' && !!tags && JSON.stringify(tags) === JSON.stringify({ anyOf: [tagId] });
};

/** The id of the saved filter to email this tag through: found, or saved now. */
export const savedFilterForTag = async (client: ApolloClient<object>, tag: { id: number; name: string }): Promise<number> => {
  const name = tagFilterName(tag.name);
  const { data } = await client.query<{ crmSegments: { items: Array<{ id: number; name: string; isActive: boolean }> } }>({
    query: CRM_TAG_FILTER_CHOICES,
    variables: { search: name },
    fetchPolicy: 'network-only',
  });
  const named = data.crmSegments.items.filter((s) => s.isActive && s.name === name);
  for (const candidate of named) {
    const detail = await client.query<{ crmList: { id: number; filter: Record<string, unknown> | null } }>({
      query: CRM_LIST_DETAIL,
      variables: { id: candidate.id },
      fetchPolicy: 'network-only',
    });
    if (isTagFilter(detail.data.crmList?.filter, tag.id)) return candidate.id;
  }
  const made = await client.mutate<{ crmSaveFilter: { id: number } }>({
    mutation: CRM_SAVE_FILTER,
    variables: { name, filter: tagFilter(tag.id), description: `Everyone tagged ${tag.name}. Saved to email them from the Tags page.` },
  });
  client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmSegments' });
  client.cache.gc();
  return made.data!.crmSaveFilter.id;
};
